import { useMemo, type ReactNode } from 'react';
import { Table2, BarChart3 } from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardBody,
} from '@vnetwork/ui/primitives';
import { Stat, DataTable, EmptyState, type ColumnDef } from '@vnetwork/ui/data';
import { Chart, PieChart, type ChartSeries } from '@vnetwork/ui/charts';
import { Alert } from '@vnetwork/ui/feedback';
import {
  formatBps,
  formatBytes,
  formatNum,
  formatPct,
} from '@/pages/services/_shared/analytics-format';
import type {
  WaapChart,
  WaapNumberChart,
  WaapPieChart,
  WaapTableChart,
  WaapTableRow,
  WaapTimeseriesChart,
} from '@/api/waap';
import styles from './WaapChartWidget.module.css';

/** Plain-number format that keeps up to 2 decimals for fractional values (e.g.
 *  QPS 0.2 / 3.41) while using thousands separators for integers/large numbers. */
function smartNum(v: number | undefined): string {
  if (v == null || !isFinite(v)) return '—';
  if (Number.isInteger(v)) return formatNum(v);
  if (Math.abs(v) >= 1000) return formatNum(Math.round(v));
  return String(Math.round(v * 100) / 100);
}

/** Pick a value formatter from the BE `unit` hint. */
function unitFormatter(unit: string): (v: number | undefined) => string {
  if (unit === 'bps') return formatBps;
  if (unit === 'B') return formatBytes;
  if (unit === 'pct') return (v) => (v == null || !isFinite(v) ? '—' : `${(v * 100).toFixed(1)}%`);
  return smartNum;
}

/** Scalar KPI value formatted by its unit (null → em dash). */
function formatNumberValue(c: WaapNumberChart): string {
  if (c.value == null) return '—';
  return unitFormatter(c.unit)(c.value);
}

/**
 * Renders a single normalized WAAP dashboard widget from the BE `charts`
 * contract, switching on `kind` (number | pie | table | timeseries). Every
 * widget is wrapped in a titled `Card`; a per-widget `error` surfaces as an
 * inline warning while the rest of the dashboard keeps rendering.
 */
export function WaapChartWidget({ chart }: { chart: WaapChart }) {
  if (chart.error) {
    return (
      <Panel title={chart.title}>
        <Alert tone="warning" title={chart.title}>
          {chart.error}
        </Alert>
      </Panel>
    );
  }

  switch (chart.kind) {
    case 'number':
      return <NumberWidget chart={chart} />;
    case 'pie':
      return <PieWidget chart={chart} />;
    case 'table':
      return <TableWidget chart={chart} />;
    case 'timeseries':
      return <TimeseriesWidget chart={chart} />;
    default:
      return null;
  }
}

/** Titled card container shared by every widget kind. */
function Panel({ title, children }: { title: string; children: ReactNode }) {
  return (
    <Card className={styles.panel}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardBody>{children}</CardBody>
    </Card>
  );
}

function NumberWidget({ chart }: { chart: WaapNumberChart }) {
  return (
    <Card className={styles.panel}>
      <CardBody>
        <Stat label={chart.title} value={formatNumberValue(chart)} />
      </CardBody>
    </Card>
  );
}

function PieWidget({ chart }: { chart: WaapPieChart }) {
  const slices = chart.slices ?? [];
  return (
    <Panel title={chart.title}>
      {slices.length === 0 ? (
        <EmptyState
          size="sm"
          icon={<BarChart3 size={20} />}
          title="No data"
          description="No data for the selected range."
        />
      ) : (
        <PieChart
          data={slices}
          height={240}
          donut
          showLegend
          valueFormatter={(v) => formatNum(typeof v === 'number' ? v : Number(v))}
        />
      )}
    </Panel>
  );
}

/** True when a chart title represents malicious-request data and should render
 *  in the danger intent color rather than the default palette. */
function isMaliciousChart(title: string): boolean {
  return /malicious/i.test(title);
}

/** Time-series trend, rendered with the shared @vnetwork/ui Chart (repo
 *  convention). The x-axis already arrives as sorted date strings from the BE;
 *  valueFormatter keeps decimals + units on the y-axis and tooltip.
 *
 *  Charts whose title signals malicious-request data (e.g. "Malicious Requests
 *  Distribution") override every series color to the danger intent token so the
 *  red/danger semantic is clear in both light and dark themes. */
function TimeseriesWidget({ chart }: { chart: WaapTimeseriesChart }) {
  const fmt = useMemo(() => unitFormatter(chart.unit), [chart.unit]);
  // The BE omits empty series/data (omitempty) when a query returns no rows
  // (e.g. a domain/range with no traffic). Guard so <Chart> never .map()s an
  // undefined series.
  const rawSeries = chart.series ?? [];
  const data = chart.data ?? [];

  // For malicious-request charts use the danger intent token so users immediately
  // read the color as "threat". Other series (benign / total) keep the default
  // palette supplied by <Chart>; this branch applies only when the entire chart
  // represents malicious traffic (single-concept chart, all series are danger).
  const series = useMemo<ChartSeries[]>(
    () =>
      isMaliciousChart(chart.title)
        ? rawSeries.map((s) => ({ ...s, color: 'var(--vn-danger-solid)' }))
        : rawSeries,
    [chart.title, rawSeries],
  );

  if (series.length === 0 || data.length === 0) {
    return (
      <Panel title={chart.title}>
        <EmptyState
          size="sm"
          icon={<BarChart3 size={20} />}
          title="No data"
          description="No data for the selected range."
        />
      </Panel>
    );
  }
  return (
    <Panel title={chart.title}>
      <Chart
        type="line"
        curve="linear"
        data={data}
        xKey={chart.xKey}
        series={series}
        height={240}
        showGrid
        valueFormatter={(v) => fmt(typeof v === 'number' ? v : Number(v))}
      />
    </Panel>
  );
}

function TableWidget({ chart }: { chart: WaapTableChart }) {
  const columns = useMemo<ColumnDef<WaapTableRow, unknown>[]>(
    () =>
      (chart.columns ?? []).map((col) => ({
        accessorKey: col.key,
        header: col.label,
        cell: ({ row }) => {
          const raw = row.original[col.key];
          if (col.kind === 'percent') return formatPct(raw == null ? undefined : Number(raw), '0..1');
          if (col.kind === 'number') return formatNum(raw == null ? undefined : Number(raw));
          return raw ?? '—';
        },
      })),
    [chart.columns],
  );

  return (
    <Panel title={chart.title}>
      {/* Wide logs (e.g. Threat Details, 20 cols) overflow the card; scroll
          horizontally instead of clipping/squeezing the columns. */}
      <div className={styles.tableScroll}>
        <DataTable
          columns={columns}
          data={chart.rows ?? []}
          emptyState={
            <EmptyState
              size="sm"
              icon={<Table2 size={20} />}
              title="No data"
              description="No records for the selected period."
            />
          }
        />
      </div>
    </Panel>
  );
}
